import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Preloader, Text } from '@common-fe/common-fe';

import ROUTES from '@/common/routes';
import { DashboardModalsKeys } from '@/modules/core/components/Dashboard/components/DashboardModals/dashboardModals.types';
import { useHistory } from '@/modules/core/hooks';
import SignIn from '@/modules/user/components/SignIn';
import VerifyBlock from '@/modules/user/components/VerifyBlock/VerifyBlock';
import useConfirmEmailQuery from '@/modules/user/queries/useConfirmEmail.query';
import { useAuthStore } from '@/modules/user/stores';
import { useQueryParams } from '@/utils/hooks';

const EMAIL_QUERY_KEY = 'email';
const HASH_ID_QUERY_KEY = 'hash_id';
const CODE_QUERY_KEY = 'confirm_code';

const EmailConfirmation = () => {
  const { t } = useTranslation();
  const query = useQueryParams();
  const history = useHistory();
  const { inited, auth } = useAuthStore();
  const { confirmUserEmail, isLoading, isSuccess } = useConfirmEmailQuery();

  const isUserAuthorized = useMemo(() => auth && inited, [auth, inited]);
  const email = useMemo(() => query.get(EMAIL_QUERY_KEY), [query]);
  const code = useMemo(() => query.get(CODE_QUERY_KEY), [query]);
  const hashId = useMemo(() => query.get(HASH_ID_QUERY_KEY), [query]);

  const handleShowSuccessPopup = useCallback(() => {
    if (isUserAuthorized) {
      history.push(`${ROUTES.HOME}#${DashboardModalsKeys.EmailSuccessfullyVerified}`);
    }
  }, [history, isUserAuthorized]);

  const handleConfirmUserEmail = useCallback(async () => {
    if (!email || !code || !hashId) {
      return;
    }

    if (!isUserAuthorized) {
      // removes parameters from url
      history.push(ROUTES.EMAIL_CONFIRMATION);
    }

    try {
      await confirmUserEmail({ code, email, hashId });
      handleShowSuccessPopup();
    } catch {
      alert('Something went wrong.\nPossible your email already verified.');
    }
  }, [email, code, hashId, isUserAuthorized, history, confirmUserEmail, handleShowSuccessPopup]);

  useEffect(() => {
    handleConfirmUserEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isUserAuthorized) {
      history.push(ROUTES.HOME);
    }
  }, [history, isUserAuthorized]);

  if (isUserAuthorized) {
    return null;
  }

  return (
    <SignIn
      header={
        isLoading ? (
          <Preloader />
        ) : (
          isSuccess && (
            <Box margin={{ bottom: 'spacing24' }}>
              <VerifyBlock>
                <Text color="textOnColor" style={{ lineHeight: '20px' }}>
                  {t('Your email address has been verified!')}
                </Text>
                <Text color="textOnColor" style={{ lineHeight: '20px' }}>
                  {t('Please login using your credentials.')}
                </Text>
              </VerifyBlock>
            </Box>
          )
        )
      }
    />
  );
};

export default EmailConfirmation;
